import { useRuntimeConfig } from "nuxt/app";
import { strippedURL } from "./strippedURL";

type StoryblokLink = {
  story?: { url?: string };
  cached_url?: string;
  url?: string;
  anchor?: string;
  id?: string;
  target?: string;
};

type SubMenuItem = {
  link_label?: string;
  link_url?: StoryblokLink;
};

type SubMenuGridColumn = {
  link_label?: string;
  link_url?: StoryblokLink;
};

type SubMenuGridItem = {
  column?: SubMenuGridColumn[];
};

type MainMenuItem = {
  label?: string;
  link?: StoryblokLink | string;
  sub_menu?: SubMenuItem[];
  sub_menu_grid?: SubMenuGridItem[];
};

type HeaderContent = {
  main_menu?: MainMenuItem[];
};

type SiteNavigationElement = {
  "@type": "SiteNavigationElement";
  name: string;
  url?: string;
  hasPart?: SiteNavigationElement[];
};

const isInvalidUrl = (url: string) =>
  !url ||
  url.includes("javascript") ||
  url.startsWith("tel:") ||
  url.startsWith("mailto:");

const toAbsoluteUrl = (link: StoryblokLink | string | undefined, baseURL: string) => {
  if (!link) return null;

  const path = strippedURL(link);
  if (isInvalidUrl(path)) return null;

  if (path.startsWith("http")) return path;
  return baseURL + (path === "/" ? "" : path);
};

const buildChildNavItem = (
  label: string | undefined,
  link: StoryblokLink | undefined,
  baseURL: string
): SiteNavigationElement | null => {
  if (!label) return null;

  const url = toAbsoluteUrl(link, baseURL);
  if (!url) return null;

  return {
    "@type": "SiteNavigationElement",
    name: label,
    url,
  };
};

const buildSubMenuChildren = (menuItem: MainMenuItem, baseURL: string) => {
  const children: SiteNavigationElement[] = [];

  menuItem.sub_menu?.forEach((subItem) => {
    const child = buildChildNavItem(subItem.link_label, subItem.link_url, baseURL);
    if (child) children.push(child);
  });

  menuItem.sub_menu_grid?.forEach((gridItem) => {
    gridItem.column?.forEach((columnItem) => {
      if (!columnItem.link_url?.id) return;

      const child = buildChildNavItem(columnItem.link_label, columnItem.link_url, baseURL);
      if (child) children.push(child);
    });
  });

  return children;
};

const buildMainMenuItem = (menuItem: MainMenuItem, baseURL: string): SiteNavigationElement | null => {
  if (!menuItem.label) return null;

  const hasSubMenu = Boolean(menuItem.sub_menu?.length || menuItem.sub_menu_grid?.length);
  const item: SiteNavigationElement = {
    "@type": "SiteNavigationElement",
    name: menuItem.label,
  };

  if (hasSubMenu) {
    const children = buildSubMenuChildren(menuItem, baseURL);
    if (children.length) {
      item.hasPart = children;
    }
  } else if (menuItem.link) {
    const url = toAbsoluteUrl(menuItem.link, baseURL);
    if (!url) return null;
    item.url = url;
  } else {
    return null;
  }

  if (!item.url && !item.hasPart?.length) return null;

  return item;
};

export const buildSiteNavigationStructuredData = (headerContent: HeaderContent | null | undefined) => {
  const config = useRuntimeConfig();
  const baseURL = config.public["baseURL"];
  const hasPart = (headerContent?.main_menu || [])
    .map((menuItem) => buildMainMenuItem(menuItem, baseURL))
    .filter((item): item is SiteNavigationElement => item !== null);

  if (!hasPart.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    hasPart,
  };
};
